
import {
  DASHBOARD_OVERVIEW_MERGE_KEYS,
  getDashboardWidgetKeysForRole,
  isFeatureDocKeyAllowedForRole,
  isLeftoverFeatureDocKey,
  resolveFeatureDocKeyForRole,
} from "./abacPermissionUtils";

export function getAbacValueAtPath(node, path) {
  if (!path?.length) return node;
  return path.reduce(
    (current, key) =>
      current && typeof current === "object" ? current[key] : undefined,
    node
  );
}

export function hasAbacPermission(permissions, path, options = {}) {
  const { defaultAllow = false } = options;
  if (!permissions || typeof permissions !== "object") return defaultAllow;

  const value = getAbacValueAtPath(permissions, path);
  if (typeof value === "boolean") return value;
  if (value && typeof value === "object" && typeof value.view === "boolean") {
    return value.view;
  }
  return defaultAllow;
}

export function isTruthyFlag(value) {
  if (value === true || value === 1) return true;
  const normalized = String(value ?? "")
    .trim()
    .toLowerCase();
  return normalized === "true" || normalized === "yes" || normalized === "1";
}

/** Walk nested objects for multiUserAccess (login/profile shapes vary). */
function findMultiUserAccessValue(node, depth = 0) {
  if (!node || typeof node !== "object" || depth > 6) return undefined;
  if (Object.prototype.hasOwnProperty.call(node, "multiUserAccess")) {
    return node.multiUserAccess;
  }
  if (Array.isArray(node)) {
    for (const item of node) {
      const found = findMultiUserAccessValue(item, depth + 1);
      if (found !== undefined) return found;
    }
    return undefined;
  }
  for (const value of Object.values(node)) {
    if (value && typeof value === "object") {
      const found = findMultiUserAccessValue(value, depth + 1);
      if (found !== undefined) return found;
    }
  }
  return undefined;
}

/**
 * @returns {boolean|null} true / false when flag found, null when missing
 */
export function getMultiUserAccessTriState(...sources) {
  for (const source of sources) {
    if (!source || typeof source !== "object") continue;
    const direct = [
      source.multiUserAccess,
      source.webMasterEntry?.multiUserAccess,
      source.webmasterEntry?.multiUserAccess,
      source.userEntry?.webMasterEntry?.multiUserAccess,
      source.userEntry?.multiUserAccess,
      source.configuration?.multiUserAccess,
    ];
    for (const candidate of direct) {
      if (candidate !== undefined && candidate !== null && candidate !== "") {
        return isTruthyFlag(candidate);
      }
    }
    const deep = findMultiUserAccessValue(source);
    if (deep !== undefined && deep !== null && deep !== "") {
      return isTruthyFlag(deep);
    }
  }
  return null;
}

export function getMultiUserAccessFlag(...sources) {
  return getMultiUserAccessTriState(...sources) === true;
}

/**
 * True when the *current login* is a multi-access member session.
 *
 * Enforced only for:
 * - `user.isMultiAccessMember` (set on access-name login / ABAC resolve), or
 * - non-empty `accessName` / `accessname` on the session user
 *
 * NOT enforced merely because the parent account has `multiUserAccess`
 * enabled in user config, and NOT for every tenant role. Those parents keep
 * full UI; only the multi-access member login is gated.
 */
export function isMultiAccessUser(user, _profile) {
  if (!user || typeof user !== "object") return false;
  // Set only when the access-device form is used. Persisted sessions that
  // were wrongly flagged from a JWT accessName must not stay gated.
  return user.abacMemberLogin === true;
}

export function resolveAbacChannelKey(channel) {
  const key = String(channel || "")
    .trim()
    .toLowerCase();
  return key;
}

/**
 * Only true platform operators skip ABAC.
 * Do NOT include "admin" — tenants often share that privilege tier in this app.
 */
export function isPrivilegedAbacBypassRole(user) {
  if (!user) return false;
  const role = String(
    user.role || (Array.isArray(user.roles) ? user.roles[0] : "") || ""
  )
    .toLowerCase()
    .trim();
  if (role === "tenant" || role.includes("tenant")) return false;
  return role === "superadmin" || role === "system";
}

/** ABAC UI/route gates run only for multi-access member sessions. */
export function shouldEnforceAbacForUser(user, profile, _permissions) {
  if (!user) return false;
  if (isPrivilegedAbacBypassRole(user)) return false;
  return isMultiAccessUser(user, profile);
}

/**
 * Dashboard widget id (Dashboard.jsx userDashboardGraphArray title / extra table)
 * -> ABAC widget leaf under sms.dashboard.widgets.
 * Only widgets that exist in the DU dashboard are mapped (one toggle = one UI).
 */
export const SMS_DASHBOARD_GRAPH_ABAC = {
  msgStatus: ["unknown_chart"],
  accountSummary: ["account_summary"],
  recentActivity: ["recent_activity"],
  countryWiseSub: ["operator_country"],
  senderWiseSub: ["sender_id"],
  userWiseSt: ["user_distribution"],
  smscWiseSub: ["route_percentage"],
  smscWiseDvy: ["route_distribution"],
  smscWiseSpam: ["smsc_spam"],
  userWiseSpam: ["user_spam"],
  performance: ["system_dashboard"],
  traffic: ["traffic_list"],
};

/** DU has a single dashboard (channel key "sms"). */
export const CHANNEL_DASHBOARD_GRAPH_ABAC = {
  sms: SMS_DASHBOARD_GRAPH_ABAC,
};

function widgetKeysAllow(permissions, channel, keyList) {
  return (Array.isArray(keyList) ? keyList : [keyList]).some((key) =>
    isMergedDashboardWidgetOn(permissions, channel, key)
  );
}

function readDashboardWidgetValue(permissions, channel, key) {
  const smsWidgets = getAbacValueAtPath(permissions, [
    channel,
    "dashboard",
    "widgets",
  ]);
  const smsVal =
    smsWidgets &&
    typeof smsWidgets === "object" &&
    Object.prototype.hasOwnProperty.call(smsWidgets, key)
      ? smsWidgets[key]
      : undefined;

  const overviewKey = DASHBOARD_OVERVIEW_MERGE_KEYS.includes(key) ? key : null;
  const overviewWidgets = overviewKey
    ? getAbacValueAtPath(permissions, ["overview", "widgets"])
    : undefined;
  const overviewVal =
    overviewKey &&
    overviewWidgets &&
    typeof overviewWidgets === "object" &&
    Object.prototype.hasOwnProperty.call(overviewWidgets, overviewKey)
      ? overviewWidgets[overviewKey]
      : undefined;

  return { smsVal, overviewVal };
}

/**
 * Merged tiles (Account Summary / Recent Activity) follow both JSON trees.
 * Any explicit OFF hides the tile; missing both still fails open.
 */
function isMergedDashboardWidgetOn(permissions, channel, key) {
  const { smsVal, overviewVal } = readDashboardWidgetValue(
    permissions,
    channel,
    key
  );
  const sources = [smsVal, overviewVal].filter((value) => value !== undefined);
  if (!sources.length) return true;
  // OFF wins — overview `{ view: false }` must hide even if SMS omitted the key.
  return sources.every(isDashboardWidgetEnabled);
}

function isDashboardWidgetEnabled(value) {
  if (value === true || value === 1 || value === "true") return true;
  if (value && typeof value === "object") {
    return (
      value.view === true || value.view === 1 || value.view === "true"
    );
  }
  return false;
}

function hasAnyEnabledDashboardWidgetNode(widgets) {
  if (!widgets || typeof widgets !== "object") return false;
  return Object.values(widgets).some(isDashboardWidgetEnabled);
}

export function canViewChannelDashboard(channel, permissions, user, profile) {
  const ch = resolveAbacChannelKey(channel);
  if (isPrivilegedAbacBypassRole(user)) return true;
  // Non multi-access users: Access Permissions do not apply.
  if (!shouldEnforceAbacForUser(user, profile)) return true;
  // Multi-access but permissions not loaded yet → fail open until fetch finishes.
  if (permissions == null) return true;

  const view = getAbacValueAtPath(permissions, [ch, "dashboard", "view"]);
  // Hide only when View is explicitly OFF.
  if (view === false || view === "false" || view === 0) return false;

  const smsWidgets = getAbacValueAtPath(permissions, [
    ch,
    "dashboard",
    "widgets",
  ]);
  const overviewWidgets = getAbacValueAtPath(permissions, [
    "overview",
    "widgets",
  ]);
  const hasWidgetTree =
    (smsWidgets && typeof smsWidgets === "object") ||
    (overviewWidgets && typeof overviewWidgets === "object");
  if (hasWidgetTree) {
    const role = user?.role || (Array.isArray(user?.roles) ? user.roles[0] : "");
    const allowed = getDashboardWidgetKeysForRole(role);
    if (allowed == null) {
      return (
        hasAnyEnabledDashboardWidgetNode(smsWidgets) ||
        hasAnyEnabledDashboardWidgetNode(overviewWidgets)
      );
    }
    return allowed.some((key) =>
      isMergedDashboardWidgetOn(permissions, ch, key)
    );
  }

  if (view === true || view === "true" || view === 1) return true;
  return true;
}

export function canViewChannelDashboardWidget(
  channel,
  permissions,
  graphId,
  user,
  profile
) {
  const ch = resolveAbacChannelKey(channel);
  if (permissions == null) {
    return true;
  }
  if (!canViewChannelDashboard(ch, permissions, user, profile)) return false;
  if (isPrivilegedAbacBypassRole(user)) return true;
  if (!shouldEnforceAbacForUser(user, profile, permissions)) return true;

  const map = CHANNEL_DASHBOARD_GRAPH_ABAC[ch] || {};
  const keys = map[graphId];
  if (!keys) return true;
  return widgetKeysAllow(
    permissions,
    ch,
    Array.isArray(keys) ? keys : [keys]
  );
}

export function canViewChannelDashboardMetrics(
  channel,
  permissions,
  user,
  profile
) {
  const ch = resolveAbacChannelKey(channel);
  if (permissions == null) {
    return true;
  }
  if (!canViewChannelDashboard(ch, permissions, user, profile)) return false;
  if (isPrivilegedAbacBypassRole(user)) return true;
  if (!shouldEnforceAbacForUser(user, profile, permissions)) return true;

  const widgets = getAbacValueAtPath(permissions, [
    ch,
    "dashboard",
    "widgets",
  ]);
  if (!widgets || typeof widgets !== "object") return true;

  // KPI row visibility follows the Metrics widget only.
  // metrics_percentage only controls the % values inside those cards.
  const metricKeys = ["summary_metrics"];

  const present = metricKeys.filter((key) =>
    Object.prototype.hasOwnProperty.call(widgets, key)
  );
  if (present.length === 0) return true;
  return present.some((key) =>
    hasAbacPermission(permissions, [ch, "dashboard", "widgets", key])
  );
}

/**
 * Whether channel dashboard KPI cards may show their percentage values.
 * Independent of Metrics row visibility; gated by widgets.metrics_percentage.
 */
export function canViewChannelDashboardMetricsPercentage(
  channel,
  permissions,
  user,
  profile
) {
  const ch = resolveAbacChannelKey(channel);
  if (permissions == null) return true;
  if (!canViewChannelDashboard(ch, permissions, user, profile)) return false;
  if (isPrivilegedAbacBypassRole(user)) return true;
  if (!shouldEnforceAbacForUser(user, profile, permissions)) return true;

  const widgets = getAbacValueAtPath(permissions, [
    ch,
    "dashboard",
    "widgets",
  ]);
  if (!widgets || typeof widgets !== "object") return true;
  if (!Object.prototype.hasOwnProperty.call(widgets, "metrics_percentage")) {
    return true;
  }
  return hasAbacPermission(permissions, [
    ch,
    "dashboard",
    "widgets",
    "metrics_percentage",
  ]);
}

export function canViewSmsDashboard(permissions, user, profile) {
  return canViewChannelDashboard("sms", permissions, user, profile);
}

export function canViewSmsDashboardWidget(permissions, graphId, user, profile) {
  return canViewChannelDashboardWidget(
    "sms",
    permissions,
    graphId,
    user,
    profile
  );
}

export function canViewSmsDashboardMetrics(permissions, user, profile) {
  return canViewChannelDashboardMetrics("sms", permissions, user, profile);
}

/** True if any boolean leaf under node is true. */
export function hasAnyTruthyPermission(node) {
  if (node === true || node === 1 || node === "true") return true;
  if (node === false || node == null) return false;
  if (typeof node !== "object") return false;
  return Object.values(node).some((v) => hasAnyTruthyPermission(v));
}

/** API/UI leaf aliases (company.edit ↔ update, module.view ↔ list). */
function abacLeafKeyAliases(key) {
  const k = String(key || "").trim();
  if (!k) return [];
  if (k === "edit") return ["edit", "update"];
  if (k === "update") return ["update", "edit"];
  if (k === "view") return ["view", "list"];
  if (k === "list") return ["list", "view"];
  if (k === "balance_add") return ["balance_add", "add_balance", "balanceAdd"];
  if (k === "balance_deduct") {
    return ["balance_deduct", "deduct_balance", "balanceDeduct"];
  }
  return [k];
}

/**
 * Known company/vendor action leaves. When the module object is present but a
 * leaf key is omitted (APIs often drop `false`), treat as deny — otherwise
 * Balance Add OFF never hides the Add Balance button (fail-open on missing).
 */
const ABAC_FAIL_CLOSED_MODULE_LEAVES = {
  company: new Set([
    "view",
    "list",
    "create",
    "edit",
    "delete",
    "balance_add",
    "balance_deduct",
    "add_balance",
    "deduct_balance",
    "balanceAdd",
    "balanceDeduct",
    "company_flag",
    "company_templates",
    "company_user_list",
    "transaction_history",
  ]),
  vendor: new Set([
    "view",
    "list",
    "create",
    "edit",
    "update",
    "delete",
    "view_smsc",
  ]),
};

/** True when a nested key is missing under an object that the policy already sent. */
function isMissingAbacChildUnderExistingParent(permissions, path) {
  if (!permissions || !path?.length) return false;
  let current = permissions;
  for (let i = 0; i < path.length; i++) {
    if (!current || typeof current !== "object") return i > 0;
    if (!Object.prototype.hasOwnProperty.call(current, path[i])) {
      return i > 0;
    }
    current = current[path[i]];
  }
  return false;
}

function getAbacValueWithLeafAliases(permissions, resolved) {
  const direct = getAbacValueAtPath(permissions, resolved);
  if (direct !== undefined) return direct;
  if (resolved.length < 2) return undefined;
  const last = resolved[resolved.length - 1];
  const parentPath = resolved.slice(0, -1);
  for (const alias of abacLeafKeyAliases(last)) {
    if (alias === last) continue;
    const alt = getAbacValueAtPath(permissions, [...parentPath, alias]);
    if (alt !== undefined) return alt;
  }
  return undefined;
}

/**
 * General ABAC gate for multi-access sessions.
 * - Non multi-access / privileged → allow
 * - Permissions not loaded (null) → allow (fail open)
 * - Explicit false on leaf / `.view` → deny
 * - Module `.view === false` → deny all nested leaves under that module
 * - Module object without `.view` → allow if any nested permission is true
 * - Missing nested key under an existing parent object → deny (API omits false)
 * - Missing top-level module → allow
 */
export function canAccessAbac(permissions, path, user, profile) {
  if (isPrivilegedAbacBypassRole(user)) return true;
  if (!shouldEnforceAbacForUser(user, profile)) return true;
  if (permissions == null) return true;
  if (!path?.length) return true;

  const segments = Array.isArray(path)
    ? path
    : String(path)
        .split(".")
        .map((s) => s.trim())
        .filter(Boolean);

  if (!segments.length) return true;

  const resolved = segments.map((seg, i) =>
    i === 0 ? resolveAbacChannelKey(seg) : seg
  );

  // Module master off (view:false) must hide every leaf instantly — not only
  // the route check for ["company"]. Leaves like company.edit stay independent
  // when view is true/missing.
  if (resolved.length >= 2) {
    const moduleNode = getAbacValueAtPath(permissions, [resolved[0]]);
    if (
      moduleNode &&
      typeof moduleNode === "object" &&
      typeof moduleNode.view === "boolean" &&
      moduleNode.view === false
    ) {
      return false;
    }
  }

  const value = getAbacValueWithLeafAliases(permissions, resolved);
  if (value === undefined) {
    // Legacy path …/doc/view|download when doc is now a boolean leaf.
    const last = resolved[resolved.length - 1];
    if (last === "view" || last === "download" || last === "list") {
      const parent = getAbacValueAtPath(
        permissions,
        resolved.slice(0, -1)
      );
      if (typeof parent === "boolean") return parent;
    }
    // Company/Vendor: module present + known leaf missing → deny (omitted false).
    if (resolved.length === 2) {
      const moduleKey = resolved[0];
      const leafSet = ABAC_FAIL_CLOSED_MODULE_LEAVES[moduleKey];
      const moduleNode = getAbacValueAtPath(permissions, [moduleKey]);
      if (
        leafSet?.has(last) &&
        moduleNode &&
        typeof moduleNode === "object"
      ) {
        return false;
      }
    }
    // Parent object exists but this child was omitted (APIs drop `false`) → deny.
    // Unknown top-level modules still fail open.
    if (resolved.length >= 2 && isMissingAbacChildUnderExistingParent(permissions, resolved)) {
      return false;
    }
    return true;
  }
  if (value === false || value === "false" || value === 0) return false;
  if (value === true || value === "true" || value === 1) return true;

  if (value && typeof value === "object") {
    if (typeof value.view === "boolean") return value.view;
    // Legacy modules used `list` as the only module gate. When `list` sits
    // beside other actions (vendor create/update/…), List OFF must not hide
    // the whole module — any enabled action keeps access.
    if (typeof value.list === "boolean" && value.view === undefined) {
      const actionKeys = Object.keys(value).filter(
        (k) => !String(k).startsWith("_")
      );
      if (actionKeys.length === 1 && actionKeys[0] === "list") {
        return value.list;
      }
      return hasAnyTruthyPermission(value);
    }
    return hasAnyTruthyPermission(value);
  }

  return true;
}

/** Allow if any of the given paths is allowed. */
export function canAccessAbacAny(permissions, paths, user, profile) {
  if (!paths?.length) return true;
  return paths.some((path) => canAccessAbac(permissions, path, user, profile));
}

/** SMS template leaf: view | create | update | delete | download | publish. */
export function canAccessSmsTemplate(
  permissions,
  action = "view",
  user,
  profile
) {
  const leaf = String(action || "view").trim().toLowerCase() || "view";
  return canAccessAbac(permissions, ["sms", "template", leaf], user, profile);
}

/** True if any template leaf is allowed (sidebar / route gate). */
export function canAccessAnySmsTemplateAction(permissions, user, profile) {
  return ["view", "create", "update", "delete", "download", "publish"].some(
    (action) => canAccessSmsTemplate(permissions, action, user, profile)
  );
}

/** Navbar / common helpers (flat tree after extractPermissionsFromResponse). */
export function canAccessNavbarItem(permissions, key, user, profile) {
  return canAccessAbac(permissions, ["navbar", key], user, profile);
}

/** Alias pairs for API snake_case vs UI camelCase action keys. */
function navbarActionKeyAliases(actionKey) {
  const key = String(actionKey ?? "").trim();
  if (!key) return [];
  const aliases = [key];
  if (key.includes("_")) {
    aliases.push(
      key.replace(/_([a-z])/g, (_, char) => String(char).toUpperCase())
    );
  } else if (/[A-Z]/.test(key)) {
    aliases.push(key.replace(/[A-Z]/g, (m) => `_${m.toLowerCase()}`));
  }
  return [...new Set(aliases)];
}

/**
 * Granular navbar action check, e.g. notifications.clear_all / markRead / delete.
 * If the action key is missing from the policy → fail open.
 * If any alias is present and false → deny.
 */
export function canAccessNavbarPermission(
  permissions,
  itemKey,
  actionKey,
  user,
  profile
) {
  if (isPrivilegedAbacBypassRole(user)) return true;
  if (!shouldEnforceAbacForUser(user, profile)) return true;
  if (permissions == null) return true;

  const aliases = navbarActionKeyAliases(actionKey);
  let sawDefined = false;
  for (const alias of aliases) {
    const path = ["navbar", itemKey, alias];
    const value = getAbacValueAtPath(permissions, path);
    if (value === undefined) continue;
    sawDefined = true;
    if (canAccessAbac(permissions, path, user, profile)) return true;
  }
  if (sawDefined) return false;
  return true;
}

/** Navbar balance chips — `navbar.wallet_balance` only. */
export function canAccessWalletBalance(permissions, user, profile) {
  if (isPrivilegedAbacBypassRole(user)) return true;
  if (!shouldEnforceAbacForUser(user, profile)) return true;
  if (permissions == null) return true;
  const value = getAbacValueAtPath(permissions, ["navbar", "wallet_balance"]);
  if (value === undefined) {
    // Legacy: some policies only define wallet without wallet_balance.
    const wallet = getAbacValueAtPath(permissions, ["navbar", "wallet"]);
    if (wallet === undefined) return true;
    return canAccessAbac(permissions, ["navbar", "wallet"], user, profile);
  }
  return canAccessAbac(permissions, ["navbar", "wallet_balance"], user, profile);
}

/** @deprecated Prefer canAccessWalletBalance for chips / canAccessWalletPage for /wallet. */
export function canAccessWallet(permissions, user, profile) {
  return canAccessWalletBalance(permissions, user, profile);
}

/** Wallet page (`/wallet`) — `navbar.wallet.view`. */
export function canAccessWalletPage(permissions, user, profile) {
  if (isPrivilegedAbacBypassRole(user)) return true;
  if (!shouldEnforceAbacForUser(user, profile)) return true;
  if (permissions == null) return true;
  const wallet = getAbacValueAtPath(permissions, ["navbar", "wallet"]);
  if (wallet === undefined) {
    const rootWallet = getAbacValueAtPath(permissions, ["wallet"]);
    if (rootWallet === undefined) return true;
    return canAccessAbac(permissions, ["wallet"], user, profile);
  }
  return canAccessAbac(permissions, ["navbar", "wallet"], user, profile);
}

function walletAbacPath(permissions, leafPath) {
  if (getAbacValueAtPath(permissions, ["navbar", "wallet"]) !== undefined) {
    return ["navbar", "wallet", ...leafPath];
  }
  if (getAbacValueAtPath(permissions, ["wallet"]) !== undefined) {
    return ["wallet", ...leafPath];
  }
  if (getAbacValueAtPath(permissions, ["common", "wallet"]) !== undefined) {
    return ["common", "wallet", ...leafPath];
  }
  return null;
}

export function canAccessWalletDownload(permissions, user, profile) {
  if (isPrivilegedAbacBypassRole(user)) return true;
  if (!shouldEnforceAbacForUser(user, profile)) return true;
  if (permissions == null) return true;
  const path = walletAbacPath(permissions, ["download"]);
  if (!path) return true;
  return canAccessAbac(permissions, path, user, profile);
}

export function canAccessWalletPricing(permissions, user, profile) {
  if (isPrivilegedAbacBypassRole(user)) return true;
  if (!shouldEnforceAbacForUser(user, profile)) return true;
  if (permissions == null) return true;
  const path = walletAbacPath(permissions, ["pricing"]);
  if (!path) return true;
  return canAccessAbac(permissions, path, user, profile);
}

export function canAccessWalletPricingDownload(permissions, user, profile) {
  if (isPrivilegedAbacBypassRole(user)) return true;
  if (!shouldEnforceAbacForUser(user, profile)) return true;
  if (permissions == null) return true;
  const path = walletAbacPath(permissions, ["pricing", "download"]);
  if (!path) return true;
  return canAccessAbac(permissions, path, user, profile);
}

export function canAccessDefaultSetting(permissions, key, user, profile) {
  return canAccessAbac(permissions, ["default", key], user, profile);
}

export const PLATFORM_DOC_API_CARD_ABAC = {
  hlrApi: "hlr_api",
  smsApi: "sms_api",
};

/** ApiRunner top-level folder name → cpaas_business_api.* */
export const PLATFORM_DOC_BUSINESS_API_FOLDER_ABAC = {
  SMS: "sms_business_api",
  "LEGACY SMS": "sms_business_api",
};

export function getPlatformDocApiCardAbacKey(cardKey) {
  return PLATFORM_DOC_API_CARD_ABAC[cardKey] || null;
}

export function getPlatformDocBusinessApiAbacKey(folderName) {
  const raw = String(folderName || "").trim();
  if (!raw) return null;
  const upper = raw.toUpperCase();
  if (PLATFORM_DOC_BUSINESS_API_FOLDER_ABAC[upper]) {
    return PLATFORM_DOC_BUSINESS_API_FOLDER_ABAC[upper];
  }
  const normalized = upper.replace(/\s+/g, " ");
  return PLATFORM_DOC_BUSINESS_API_FOLDER_ABAC[normalized] || null;
}

export function getPlatformDocApiCardAbacKeys(cardKey) {
  const key = getPlatformDocApiCardAbacKey(cardKey);
  return key ? [key] : [];
}


export const PLATFORM_DOC_FEATURE_MODULE_ABAC = {
  login: ["login"],
  common_functionality: ["common_functionality"],
  dashboard: ["dashboard"],
  documentation: ["documentation"],
  messaging: ["messaging"],
  socials: ["socials", "messaging"],
  sms_utilities: ["sms_utilities"],
  reports: ["reports"],
  address_book: ["address_book"],
  templates: ["templates"],
  approvals: ["approvals"],
  user: ["user"],
  routing: ["routing"],
  company: ["company"],
  vendor: ["vendor"],
  firewall: ["firewall"],
  smsc: ["smsc", "providers"],
  dlt: ["dlt"],
  sales_teams: ["sales_teams"],
  network: ["network"],
  error_code: ["error_code"],
  user_templates: ["user_templates"],
};

function isFeatureDocLeafOn(value) {
  return value === true || value === "true" || value === 1;
}

/** Leftover tiles follow real backend leaves: show if any sibling is on. */
function hasAnyBackendFeatureDocOn(features, role) {
  if (typeof features === "boolean") return features === true;
  if (!features || typeof features !== "object") return false;
  return Object.entries(features).some(([key, value]) => {
    if (isLeftoverFeatureDocKey(key)) return false;
    if (!isFeatureDocKeyAllowedForRole(role, key)) return false;
    return isFeatureDocLeafOn(value);
  });
}

export function canAccessPlatformDocFeature(
  permissions,
  featureKey,
  user,
  profile
) {
  if (isPrivilegedAbacBypassRole(user)) return true;
  if (!shouldEnforceAbacForUser(user, profile)) return true;
  if (permissions == null) return true;

  const role = user?.role || (Array.isArray(user?.roles) ? user.roles[0] : "");
  const features = getAbacValueAtPath(permissions, [
    "platform_documents",
    "cpaas_documentation",
    "features_documents",
  ]);
  if (typeof features === "boolean") return features === true;
  const resolvedKey = resolveFeatureDocKeyForRole(role, featureKey);
  if (isLeftoverFeatureDocKey(resolvedKey)) {
    return hasAnyBackendFeatureDocOn(features, role);
  }
  if (features && typeof features === "object") {
    const aliases =
      PLATFORM_DOC_FEATURE_MODULE_ABAC[resolvedKey] || [resolvedKey];
    const present = aliases.filter((key) =>
      Object.prototype.hasOwnProperty.call(features, key)
    );
    if (!present.length) return false;
    return present.some((key) => isFeatureDocLeafOn(features[key]));
  }
  return false;
}

const PLATFORM_DOC_FEATURE_FILE_ABAC = {
  Login: "login",
  "Common Functionality": "common_functionality",
  Dashboard: "dashboard",
  Documentation: "documentation",
  Messaging: "messaging",
  Utilities: "sms_utilities",
  Reports: "reports",
  "Address Book": "address_book",
  Templates: "templates",
  Approvals: "approvals",
  User: "user",
  View: "user",
  Routing: "routing",
  Company: "company",
  Vendor: "vendor",
  Firewall: "firewall",
  SMSC: "smsc",
  DLT: "dlt",
  Error_Code: "error_code",
  "User Template": "user_templates",
  "Sales Team": "sales_teams",
  Network: "network",
};

export function canAccessFeatureDocument(permissions, fileName, user, profile) {
  const featureKey = PLATFORM_DOC_FEATURE_FILE_ABAC[fileName] || fileName;
  return canAccessPlatformDocFeature(permissions, featureKey, user, profile);
}

export function canAccessPlatformDocApiCard(
  permissions,
  cardKey,
  user,
  profile
) {
  const keys = getPlatformDocApiCardAbacKeys(cardKey);
  if (!keys.length) return true;
  return canAccessAbacAny(
    permissions,
    keys.map((key) => [
      "platform_documents",
      "cpaas_documentation",
      "api_documents",
      key,
    ]),
    user,
    profile
  );
}

/** True when at least one Features Documents tile would actually render. */
export function canViewPlatformDocumentsUi(permissions, user, profile) {
  if (isPrivilegedAbacBypassRole(user)) return true;
  if (!shouldEnforceAbacForUser(user, profile)) return true;
  if (permissions == null) return true;

  const role = user?.role || (Array.isArray(user?.roles) ? user.roles[0] : "");
  const featureKeys = [
    ...new Set(Object.values(PLATFORM_DOC_FEATURE_FILE_ABAC)),
  ];
  return featureKeys.some((featureKey) => {
    const resolvedKey = resolveFeatureDocKeyForRole(role, featureKey);
    if (isLeftoverFeatureDocKey(resolvedKey)) return false;
    if (!isFeatureDocKeyAllowedForRole(role, featureKey)) return false;
    return canAccessPlatformDocFeature(
      permissions,
      featureKey,
      user,
      profile
    );
  });
}

/** True when SDK Documentation (CPaaS Business API) is allowed. */
export function canViewSdkDocumentsUi(permissions, user, profile) {
  if (isPrivilegedAbacBypassRole(user)) return true;
  if (!shouldEnforceAbacForUser(user, profile)) return true;
  if (permissions == null) return true;
  return canAccessAbac(
    permissions,
    ["platform_documents", "cpaas_business_api"],
    user,
    profile
  );
}

/** Sidebar SMSC pages that exist for tenant (not leftover alert/http/hlr keys). */
const SMSC_SIDEBAR_SECTION_KEYS = [
  "smsc",
  "looping",
  "blocking",
  "special_setting",
  "schedule",
  "group",
];

function isSmscSectionViewOn(node) {
  if (node === true || node === "true" || node === 1) return true;
  if (node && typeof node === "object") {
    return node.view === true || node.view === "true" || node.view === 1;
  }
  return false;
}

/** True when at least one SMSC sidebar child would actually render. */
export function canViewSmscProvidersUi(permissions, user, profile) {
  if (isPrivilegedAbacBypassRole(user)) return true;
  if (!shouldEnforceAbacForUser(user, profile)) return true;
  if (permissions == null) return true;
  const providers = getAbacValueAtPath(permissions, [
    "sms",
    "smsc_providers",
  ]);
  if (typeof providers === "boolean") return providers === true;
  if (providers && typeof providers === "object") {
    return SMSC_SIDEBAR_SECTION_KEYS.some((key) =>
      isSmscSectionViewOn(providers[key])
    );
  }
  return false;
}

function isFirewallLeafOn(node) {
  if (node === true || node === "true" || node === 1) return true;
  if (node && typeof node === "object") {
    return Object.values(node).some(isFirewallLeafOn);
  }
  return false;
}

/** Extra user: Firewall sidebar follows sms.firewall leaves, not navOptions.bsfm. */
export function canViewFirewallUi(permissions, user, profile) {
  if (isPrivilegedAbacBypassRole(user)) return true;
  if (!shouldEnforceAbacForUser(user, profile)) return true;
  if (permissions == null) return true;
  const firewall = getAbacValueAtPath(permissions, ["sms", "firewall"]);
  if (firewall === undefined) return false;
  return isFirewallLeafOn(firewall);
}

/**
 * Routing Edit — page/menu open leaves.
 * *View = front Basic/Optional/MMS entry; *Update = modify inside that list.
 * apply / undo / previous stay as in-page actions (not enough alone to keep Edit open).
 */
export const ROUTING_EDIT_ACCESS_PATHS = [
  ["sms", "routing", "edit", "BasicView"],
  ["sms", "routing", "edit", "OptionalView"],
  ["sms", "routing", "edit", "MMsView"],
  ["sms", "routing", "edit", "export"],
  ["sms", "routing", "edit", "BasicUpdate"],
  ["sms", "routing", "edit", "OptionalUpdate"],
  ["sms", "routing", "edit", "MmsUpdate"],
  ["sms", "routing", "edit", "delete"],
];

export const ROUTING_COPY_ACCESS_PATHS = [
  ["sms", "routing", "copy", "view"],
  ["sms", "routing", "copy", "copy"],
];

export const ROUTING_ADD_ACCESS_PATHS = [
  ["sms", "routing", "add", "view"],
  ["sms", "routing", "add", "import"],
  ["sms", "routing", "add", "apply"],
  ["sms", "routing", "add", "create"],
];

export const ROUTING_SENDER_ACCESS_PATHS = [
  ["sms", "routing", "sender", "add", "view"],
  ["sms", "routing", "sender", "add", "create"],
  ["sms", "routing", "sender", "edit", "view"],
  ["sms", "routing", "sender", "edit", "update"],
];

export const ROUTING_MENU_ACCESS_PATHS = [
  ...ROUTING_ADD_ACCESS_PATHS,
  ...ROUTING_EDIT_ACCESS_PATHS,
  ...ROUTING_COPY_ACCESS_PATHS,
  ...ROUTING_SENDER_ACCESS_PATHS,
];

